import { useState, useEffect, useCallback } from "react";

interface TocItem {
  id: string;
  title: string;
}

export function useBlogTableOfContents(items: TocItem[]) {
  const [activeId, setActiveId] = useState<string>("");

  const handleScroll = useCallback(() => {
    if (items.length === 0) return;

    const headings = items
      .map((item) => document.getElementById(item.id))
      .filter(Boolean) as HTMLElement[];

    if (headings.length === 0) return;

    // Find the heading that's closest to the top of the viewport
    const scrollPosition = window.scrollY + 120; // Offset for sticky header

    let activeHeading = headings[0];

    for (const heading of headings) {
      if (heading.offsetTop <= scrollPosition) {
        activeHeading = heading;
      } else {
        break;
      }
    }

    setActiveId(activeHeading.id);
  }, [items]);

  useEffect(() => {
    // Set initial active item
    if (items.length > 0 && !activeId) {
      setActiveId(items[0].id);
    }

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // Initial check

    return () => window.removeEventListener("scroll", handleScroll);
  }, [items, activeId, handleScroll]);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100; // Header offset
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  return { activeId, scrollToSection };
}
